import { useState } from "react";
import { Link } from "react-router";
import { H2 } from "@/shared/ui/typographies/h2";

type CarouselSlideBaseProps = {
  tag?: React.ReactNode
  title: string
  subtitle?: string
  link?: string
  ctaText?: string
  className?: string
  onHoverChange?: (hovered: boolean) => void
};

type CarouselSlideBackgroundProps =
  | { backgroundImage: string, backgroundColor?: never }
  | { backgroundColor: string, backgroundImage?: never };

export type CarouselSlideProps = CarouselSlideBaseProps & CarouselSlideBackgroundProps;

export const CarouselSlide = ({
  backgroundImage,
  backgroundColor,
  tag,
  title,
  subtitle,
  link,
  ctaText,
  className = "",
  onHoverChange,
}: CarouselSlideProps) => {
  const [imageLoadError, setImageLoadError] = useState(false);

  const handleMouseEnter = () => {
    onHoverChange?.(true);
  };

  const handleMouseLeave = () => {
    onHoverChange?.(false);
  };

  const handleImageError = () => {
    setImageLoadError(true);
  };

  const useBackgroundColor = backgroundColor || imageLoadError;
  const finalBackgroundColor = backgroundColor || "#1a1a1a";

  const backgroundStyle = useBackgroundColor
    ? { backgroundColor: finalBackgroundColor }
    : {
      backgroundImage: `url(${backgroundImage})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    };

  const hasCta = ctaText && link;

  return (
    <div
      role="region"
      aria-label={`${title}${subtitle ? `: ${subtitle}` : ""}`}
      aria-roledescription="slide"
      className={`relative h-full w-full overflow-hidden rounded-lg ${className}`}
      style={backgroundStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {backgroundImage && !imageLoadError && (
        <img
          src={backgroundImage}
          alt=""
          aria-hidden="true"
          loading="lazy"
          onError={handleImageError}
          className="absolute inset-0 h-full w-full object-cover opacity-0"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/70" />

      <div className="relative flex h-full flex-col justify-between px-10 py-6 text-white sm:px-8 sm:py-10 md:justify-between md:px-10 md:py-10 lg:px-20">
        <div className="flex flex-col gap-1.5 sm:gap-2 md:mt-auto md:gap-3">
          {tag && (
            <div className="w-fit pt-1 sm:pt-2">
              {tag}
            </div>
          )}

          <div className="flex flex-col gap-1.5 sm:gap-2 md:gap-3">
            <H2 className="font-bold leading-tight text-xl text-white sm:text-2xl md:text-4xl lg:text-5xl">
              {title}
            </H2>

            {subtitle && (
              <p className="text-sm font-medium text-white/90 sm:text-base md:text-lg">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {hasCta && (
          <div className="mt-auto md:mt-4">
            <Link
              to={link}
              className="inline-flex w-fit items-center justify-center rounded-full bg-white/100 px-3 py-1.5 text-xs font-semibold text-dark transition-all duration-200 hover:bg-primary hover:text-white hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-transparent active:scale-95 sm:px-4 sm:py-2 sm:text-sm md:px-6 md:py-2.5 md:text-base"
            >
              {ctaText}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};
